import {AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from "@angular/router";
import {MatSnackBar} from "@angular/material/snack-bar";
import {MatDialog} from "@angular/material/dialog";
import {AuthService} from "../../../../shared/services/auth.service";
import {FilterService} from "../../../../shared/services/filter.service";
import {select, Store} from "@ngrx/store";
import {StoreRootState} from "../../../../shared/ngrx/reducers";
import {ReplaySubject} from "rxjs";
import {delay, distinctUntilChanged, takeUntil, tap} from "rxjs/operators";
import {MatPaginator, PageEvent} from "@angular/material/paginator";
import {getCurrentRouteState} from "../../../../shared/ngrx/selector";
import {RouterStateUrl} from "../../../../shared/ngrx/route-serializer";
import {NgForm} from "@angular/forms";
import {Invoice} from "../../../../shared/interface/invoice";
import {ContractBalance} from "../../../../shared/interface/contract-balance";
import {UserPermissions} from "../../../../shared/interface/user-permissions";
import {CountedAndPaginated} from "../../../../shared/interface/response/counted-and-paginated";
import {Uuid} from "../../../../shared/interface/response/uuid";
import {JobResultDialogComponent} from "../../../../shared/components/job-result-dialog/job-result-dialog.component";
import {HttpErrorResponse} from "@angular/common/http";
import {ChangeStatusDialogComponent} from "../../invoices/change-status-dialog/change-status-dialog.component";
import {Sort} from "@angular/material/sort";
import {ContractBalanceService} from "../../../../shared/services/contract-balance.service";

@Component({
  selector: 'app-all-contract-balance-list',
  templateUrl: './all-contract-balance-list.component.html',
  styleUrls: ['./all-contract-balance-list.component.scss']
})
export class AllContractBalanceListComponent implements OnInit, OnDestroy, AfterViewInit  {
  @Input() embeddedView = false;
  @ViewChild('filtersForm') filtersForm: NgForm;
  @ViewChild(MatPaginator) paginator: MatPaginator;

  initialFilters: any;
  items: ContractBalance[] = [];
  totalData = 0;
  displayedColumns: string[] = [
    'party', 'contractOwner', 'number', 'company', 'contractType',  'status',  'toPayNet', 'paid',
  ];

  userPermissions: ReplaySubject<UserPermissions>;
  sortBy = 'toPayNet';
  sortOrder = 'desc';


  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private contractBalanceService: ContractBalanceService,
    private snack: MatSnackBar,
    private dialog: MatDialog,
    private auth: AuthService,
    private activatedRoute: ActivatedRoute,
    private filterService: FilterService,
    private store: Store<StoreRootState>,
    private router: Router,
  ) {
    this.userPermissions = auth.getUserPermissions();
    this.contractBalanceService.contractBalanceListSubject
      .pipe(
        takeUntil(this.destroyed$),
        tap((e: CountedAndPaginated<ContractBalance>) => {
          this.items = e.items;
          this.totalData = e.pagination.total;
        })
      ).subscribe();
    this.parseFilters();
  }

  ngOnInit(): void {
  }
  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
  ngAfterViewInit(): void {
    this.paginator.page
      .pipe(
        takeUntil(this.destroyed$)
      ).subscribe((event: PageEvent) => {
      this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
        page: event.pageIndex,
        pageSize: event.pageSize,
        sortBy: this.sortBy,
        sortOrder: this.sortOrder,
        ...this.filtersForm.value
      })]);
    });

    this.store
      .pipe(
        select(getCurrentRouteState),
        delay(0),
        distinctUntilChanged((prev: RouterStateUrl, curr: RouterStateUrl) => prev.url === curr.url),
        takeUntil(this.destroyed$),
      )
      .subscribe((state: RouterStateUrl) => {
        this.parseFilters(state.params);
        this.paginator.pageIndex = state.params.page;
        this.paginator.pageSize = state.params.pageSize;
        this.sortBy = state.params?.sortBy ? state.params.sortBy : 'createdAt';
        this.sortOrder = state.params?.sortOrder ? state.params.sortOrder : 'desc';
        this.loadListData();
      })
    ;
  }
  parseFilters(filters?: any): void {
    this.initialFilters = this.filterService.parseFiltersFromQueryParams({
      company: 'int',
      number: 'string',
      ofwca: 'string',
      partner: 'string',
      contractStatus: 'int[]',
      gte$net: 'string',
      lte$net: 'string',
      assetsRegional: 'string',
      contractType: 'int[]',
      gte$paid: 'string',
      lte$paid: 'string',
      gte$toPayNet: 'string',
      lte$toPayNet: 'string',
      billingMonth: 'date'

    }, filters);
  }
  buildFilters(): any {
    const filters = {...this.initialFilters};

    return filters;
  }
  filter(): void {
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
      page: 0,
      pageSize: this.paginator.pageSize,
      ...this.filtersForm.value,
    })]);
  }

  loadListData(): void {
    this.contractBalanceService.loadList(
      this.paginator.pageIndex,
      this.paginator.pageSize,
      this.buildFilters(),
      {
        sortBy: this.sortBy,
        sortOrder: this.sortOrder
      });
  }

  downloadReport(): void {
    this.contractBalanceService.downloadReport(this.buildFilters(),
      {
        sortBy: this.sortBy,
        sortOrder: this.sortOrder
      })
      .subscribe((response: Uuid) => {
        this.dialog.open(JobResultDialogComponent, {
          disableClose: true,
          width: '500px',
          data: {jobId: response.uuid, showProgress: false},
        });
      }, (error: HttpErrorResponse) => {
        this.snack.open('Przy generowaniu raportu wystąpił błąd', error.status.toString(), {duration: 2000});
      });
  }

  resetFilters(): void {
    this.filtersForm.resetForm();
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, {
      page: 0,
      pageSize: this.paginator.pageSize,
    }]);
  }

  changeStatus(
    actionCode: string,
    enableNote: boolean,
    noteLabel: string,
    newStatusName: string,
    invoice: Invoice,
  ): void {
    const dialogRef = this.dialog.open(ChangeStatusDialogComponent, {
      width: '500px',
      data: {
        invoice,
        enableNote,
        noteLabel,
        actionCode,
        newStatusName,
        sortBy: invoice,
      },
    });

    dialogRef.afterClosed().subscribe((t) => {
      this.loadListData();
    });

  }

  sortChanged($event: Sort): void {
    this.sortBy = $event.active;
    this.sortOrder = $event.direction;

    if (!this.embeddedView) {
      this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
        sortBy: $event.active,
        sortOrder: $event.direction,
        page: this.paginator.pageIndex,
        pageSize: this.paginator.pageSize,
        ...this.filtersForm.value
      })]);
    } else {
      this.loadListData();
    }
  }

  downloadInvoiceReport(): void {
    this.contractBalanceService.downloadInvoiceReport(this.buildFilters(),
      {
        sortBy: this.sortBy,
        sortOrder: this.sortOrder
      })
      .subscribe((response: Uuid) => {
        this.dialog.open(JobResultDialogComponent, {
          disableClose: true,
          width: '500px',
          data: {jobId: response.uuid, showProgress: false},
        });
      }, (error: HttpErrorResponse) => {
        this.snack.open('Przy generowaniu raportu wystąpił błąd', error.status.toString(), {duration: 2000});
      });
  }
}
